import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { Comment, Video } from '@blackblack/shared';
import { formatCount } from '@blackblack/shared';
import { api, errorMessage } from '../../lib/api';
import { patchEntity, useVideo } from '../../lib/entities';
import { resetInfinite, useInfinite } from '../../lib/query';
import { Link } from '../../lib/router';
import { useAuth } from '../../state/auth';
import { useToast } from '../../state/toast';
import { Icon } from '../../components/ui/Icon';
import { Avatar, EmptyState, LoadMore, Skeleton, Spinner, VerifiedBadge, cx } from '../../components/ui/primitives';
import { Sheet } from '../../components/ui/Sheet';
import { RichText } from '../../components/RichText';
import { timeAgo } from '../../lib/time';
import { ReportSheet } from '../moderation/ReportSheet';
import styles from './comments.module.css';

type C = Comment & { liked: boolean };

export function CommentsSheet({ video: base, onClose }: { video: Video; onClose: () => void }) {
  const video = useVideo(base);
  const { me, requireAuth } = useAuth();
  const toast = useToast();
  const list = useInfinite<C>(`comments:${video.id}`, (cursor) => api.page<C>(`/videos/${video.id}/comments`, { cursor }));
  const [local, setLocal] = useState<C[]>([]);
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState<C | null>(null);
  const [sending, setSending] = useState(false);
  const [reporting, setReporting] = useState<C | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const [repliesAdded, setRepliesAdded] = useState<Record<string, C[]>>({});
  // Comments are always fresh when the sheet is reopened.
  useEffect(() => () => {
    resetInfinite(`comments:${video.id}`);
    resetInfinite('replies:');
  }, [video.id]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!requireAuth('Log in om te reageren')) return;
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      const c = await api.post<C>(`/videos/${video.id}/comments`, { text: body, parentId: replyTo?.id });
      if (c.parentId) setRepliesAdded((r) => ({ ...r, [c.parentId!]: [...(r[c.parentId!] ?? []), c] }));
      else setLocal((l) => [c, ...l]);
      patchEntity('video', video.id, { comments: video.stats.comments + 1 });
      setText('');
      setReplyTo(null);
    } catch (err) {
      toast(errorMessage(err), { tone: 'error' });
    } finally {
      setSending(false);
    }
  };

  const remove = async (c: C) => {
    try {
      await api.delete(`/comments/${c.id}`);
      setRemoved((r) => new Set(r).add(c.id));
      patchEntity('video', video.id, { comments: Math.max(0, video.stats.comments - 1 - (c.parentId ? 0 : c.replyCount)) });
      toast('Reactie verwijderd');
    } catch (err) {
      toast(errorMessage(err), { tone: 'error' });
    }
  };

  const all = [...local, ...list.items.filter((c) => !local.some((l) => l.id === c.id))].filter((c) => !removed.has(c.id));

  return (
    <>
      <Sheet
        open
        onClose={onClose}
        title={`${formatCount(video.stats.comments)} reacties`}
        height="tall"
        footer={
          video.allowComments || me?.id === video.author.id ? (
            <form className={styles.composer} onSubmit={submit}>
              {me ? <Avatar user={me} size={32} /> : null}
              <div className={styles.inputWrap}>
                {replyTo && (
                  <button type="button" className={styles.replyingTo} onClick={() => setReplyTo(null)}>
                    Antwoord aan @{replyTo.author.username} <Icon name="close" size={12} />
                  </button>
                )}
                <input
                  ref={input}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={me ? 'Voeg een reactie toe…' : 'Log in om te reageren'}
                  maxLength={500}
                  aria-label="Reactie"
                  onFocus={() => !me && requireAuth('Log in om te reageren')}
                />
              </div>
              <button className={styles.send} disabled={!text.trim() || sending} aria-label="Plaatsen">
                {sending ? <Spinner size={18} /> : <Icon name="up" size={20} strokeWidth={2.5} />}
              </button>
            </form>
          ) : (
            <p className={styles.disabled}>Reacties staan uit voor deze video</p>
          )
        }
      >
        {list.initialLoading ? (
          <div className={styles.list}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={styles.row}>
                <Skeleton w={36} h={36} r={18} />
                <div style={{ flex: 1, display: 'grid', gap: 6 }}>
                  <Skeleton w={90} h={12} />
                  <Skeleton w="85%" h={14} />
                </div>
              </div>
            ))}
          </div>
        ) : all.length === 0 ? (
          <EmptyState icon="comment" title="Nog geen reacties" text="Begin het gesprek." />
        ) : (
          <ul className={styles.list}>
            {all.map((c) => (
              <CommentRow
                key={c.id}
                comment={c}
                videoId={video.id}
                canDelete={me?.id === c.author.id || me?.id === video.author.id}
                onReply={() => {
                  if (!requireAuth('Log in om te reageren')) return;
                  setReplyTo(c);
                  input.current?.focus();
                }}
                onDelete={remove}
                onReport={setReporting}
                extraReplies={repliesAdded[c.id] ?? []}
                removed={removed}
              />
            ))}
            <LoadMore onVisible={list.loadMore} loading={list.loading} hasMore={list.hasMore} />
          </ul>
        )}
      </Sheet>
      {reporting && <ReportSheet targetType="comment" targetId={reporting.id} onClose={() => setReporting(null)} />}
    </>
  );
}

function CommentRow({
  comment,
  videoId,
  canDelete,
  onReply,
  onDelete,
  onReport,
  extraReplies,
  removed,
  isReply,
}: {
  comment: C;
  videoId: string;
  canDelete: boolean;
  onReply: () => void;
  onDelete: (c: C) => void;
  onReport: (c: C) => void;
  extraReplies: C[];
  removed: Set<string>;
  isReply?: boolean;
}) {
  const { requireAuth, me } = useAuth();
  const [liked, setLiked] = useState(comment.liked);
  const [likes, setLikes] = useState(comment.likeCount);
  const [showReplies, setShowReplies] = useState(false);
  const replies = useInfinite<C>(showReplies ? `replies:${comment.id}` : null, (cursor) => api.page<C>(`/videos/${videoId}/comments/${comment.id}/replies`, { cursor }));

  const toggleLike = async () => {
    if (!requireAuth('Log in om reacties te liken')) return;
    const next = !liked;
    setLiked(next);
    setLikes((n) => n + (next ? 1 : -1));
    try {
      if (next) await api.post(`/comments/${comment.id}/like`);
      else await api.delete(`/comments/${comment.id}/like`);
    } catch {
      setLiked(!next);
      setLikes((n) => n + (next ? -1 : 1));
    }
  };

  const replyList = [...replies.items, ...extraReplies.filter((r) => !replies.items.some((x) => x.id === r.id))].filter((r) => !removed.has(r.id));
  const hiddenCount = comment.replyCount - (showReplies ? replies.items.length : 0);

  return (
    <li className={cx(styles.row, isReply && styles.reply)}>
      <Link to={`/u/${comment.author.username}`}>
        <Avatar user={comment.author} size={isReply ? 28 : 36} />
      </Link>
      <div className={styles.body}>
        <div className={styles.head}>
          <Link to={`/u/${comment.author.username}`} className={styles.name}>
            {comment.author.displayName}
            {comment.author.verified && <VerifiedBadge size={12} />}
          </Link>
          <span className={styles.time}>{timeAgo(comment.createdAt)}</span>
        </div>
        <p className={styles.text}>
          <RichText text={comment.text} />
        </p>
        <div className={styles.actions}>
          {!isReply && <button onClick={onReply}>Antwoorden</button>}
          {canDelete && <button onClick={() => onDelete(comment)}>Verwijderen</button>}
          {me && me.id !== comment.author.id && <button onClick={() => onReport(comment)}>Rapporteren</button>}
        </div>
        {!isReply && (replyList.length > 0 || hiddenCount > 0) && (
          <ul className={styles.replies}>
            {replyList.map((r) => (
              <CommentRow key={r.id} comment={r} videoId={videoId} canDelete={me?.id === r.author.id || canDelete} onReply={onReply} onDelete={onDelete} onReport={onReport} extraReplies={[]} removed={removed} isReply />
            ))}
            {(!showReplies && comment.replyCount > 0) || (showReplies && replies.hasMore) ? (
              <li>
                <button className={styles.more} onClick={() => (showReplies ? replies.loadMore() : setShowReplies(true))}>
                  {replies.loading ? <Spinner size={14} /> : <>— {showReplies ? 'Meer antwoorden' : `${comment.replyCount} ${comment.replyCount === 1 ? 'antwoord' : 'antwoorden'} bekijken`}</>}
                </button>
              </li>
            ) : null}
          </ul>
        )}
      </div>
      <button className={cx(styles.like, liked && styles.liked)} onClick={toggleLike} aria-label="Vind ik leuk" aria-pressed={liked}>
        <Icon name="heart" size={16} filled={liked} />
        {likes > 0 && <span>{formatCount(likes)}</span>}
      </button>
    </li>
  );
}
